import React from 'react'; // Import useState
import {
  Box,
  Stack,
  Center,
  ResponsiveValue,
  HStack,
  Text,
  useBreakpointValue,
  Button,
} from '@chakra-ui/react';
import { IoTicketOutline } from 'react-icons/io5';
import { CustomImage } from '../atoms/CustomImage'; // Reusing the existing BannerImage atom
import { CustomText } from '../atoms/CustomText'; // Reusing the existing Typography atom
import { SearchBar } from './SearchBar';
import { JaggedBorderHeader } from '../atoms/JaggedBorderHeader';
import { JaggedBorderHeaderMobile } from '../atoms/JaggedBorderHeaderMobile';

interface BannerWithSearchBarProps {
  imageSrc: string | undefined;
  altText: string;
  bannerText?: string;
  subText?: string | React.ReactNode;
  placeholderFontSize?: ResponsiveValue<string>;
  placeholderFontFamily?: string;
  placeholderText: string;
  placeholderColor?: string;
  placeholderFontWeight?: string;
  searchBarTextColor?: string;
  showOverlay?: boolean | undefined;
  showSearchBar: boolean | undefined;
  px?: ResponsiveValue<string>;
  isFullScreen?: boolean;
  searchBarPx?: ResponsiveValue<string>;
  bannerTextColor?: string;
  bgColor?: string;
  showJaggedBorder?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
  showButton?: boolean;
  secondaryAccentColor?: string;
  buttonHref?: string;
  isPreview?: boolean;
}

export const BannerWithSearchBar: React.FC<BannerWithSearchBarProps> = ({
  isFullScreen = true,
  imageSrc,
  altText,
  bannerText,
  subText,
  placeholderFontSize,
  placeholderFontFamily,
  placeholderText,
  placeholderColor,
  placeholderFontWeight,
  searchBarTextColor,
  showOverlay,
  showSearchBar = true,
  px = { base: 0, md: '1vw', lg: '15vw' },
  searchBarPx = { base: '0', md: '1vw', lg: '15vw' },
  bannerTextColor = 'white',
  bgColor = 'white',
  showJaggedBorder,
  buttonText,
  onButtonClick,
  showButton,
  secondaryAccentColor,
  buttonHref,
  isPreview,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  return (
    <Box
      position="relative"
      // minH={{ base: '220px', md: '320px', lg: '420px' }}
      sx={{
        '@media (orientation: landscape) and (max-width: 768px)': {
          height: '40vh', // or a minHeight px
          minHeight: '200px',
        },
      }}
      height={{ base: '35vh', md: '45vh', lg: '50vh' }}
      overflow="visible"
      px={px}
    >
      {/* Background Image */}
      <CustomImage src={imageSrc} alt={altText} objectFit="cover" />
      {/* Overlay */}
      <Box
        px={px}
        position="absolute"
        bottom="0"
        right={{
          base: (px as any)?.base,
          md: (px as any)?.md,
          sm: (px as any)?.sm,
          lg: (px as any)?.lg,
        }}
        left={{
          base: (px as any)?.base,
          md: (px as any)?.md,
          sm: (px as any)?.sm,
          lg: (px as any)?.lg,
        }}
        height="50%"
        bg={showOverlay ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        zIndex={1}
      />
      {/* Content */}
      <Box
        position="absolute"
        bottom="8"
        right="0"
        width="100%"
        height="50%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        zIndex={2}
      >
        <Center>
          <Stack spacing={4} width="100%">
            {/* Banner Text */}
            <CustomText
              variant="h1"
              color={bannerTextColor}
              textAlign="center"
              fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
              fontWeight="700"
            >
              {bannerText}
            </CustomText>

            {/* Subtext if provided */}
            {subText && (
              <HStack justify="center" align="center" spacing={3} flexWrap="wrap">
                {showButton && buttonText && (
                  <>
                    <a href={buttonHref}>
                      <Button
                        onClick={e => {
                          const isLeftClick = e.button === 0;
                          const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                          // SPA routing on normal click
                          if (isLeftClick && !isModifiedClick) {
                            e.preventDefault();
                            onButtonClick?.();
                          }
                        }}
                        size="sm"
                        variant="solid"
                        fontWeight="bold"
                        leftIcon={<IoTicketOutline color={secondaryAccentColor} size={18} />}
                        fontSize="md"
                        padding="8px 16px"
                        borderRadius="0"
                        type="button"
                        colorScheme="black"
                      >
                        {buttonText}
                      </Button>
                    </a>
                    <Text color={secondaryAccentColor}>/</Text>
                  </>
                )}

                <CustomText
                  variant="body"
                  color={bannerTextColor}
                  textAlign="center"
                  fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}
                >
                  {subText}
                </CustomText>
              </HStack>
            )}

            {showSearchBar && (
              <SearchBar
                isFullScreen={isFullScreen}
                searchBarPx={searchBarPx}
                searchBarTextColor={searchBarTextColor}
                searchBarDropDownTextColor={secondaryAccentColor}
                placeholderText={placeholderText}
                placeholderColor={placeholderColor}
                placeholderFontSize={placeholderFontSize}
                placeholderFontFamily={placeholderFontFamily}
                iconColor={secondaryAccentColor}
                placeholderFontWeight={placeholderFontWeight}
                isPreview={isPreview}
              />
              // </Box>
            )}
          </Stack>
        </Center>
      </Box>
      {showJaggedBorder &&
        (isMobile ? (
          <JaggedBorderHeaderMobile bgColor={bgColor} />
        ) : (
          <JaggedBorderHeader bgColor={bgColor} />
        ))}{' '}
    </Box>
  );
};
